import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { EmptyState } from '../../components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Search,
  Star,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  Truck,
  Mail,
  Phone,
  Filter,
  Plus,
  Eye,
  MapPin
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const AdminLeveranciers = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leveranciers, setLeveranciers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, leverancier: null });
  const [processing, setProcessing] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, leverancier: null });

  useEffect(() => {
    const fetchLeveranciers = async () => {
      try {
        const response = await api.get('/admin/partners');
        setLeveranciers(response.data);
      } catch (error) {
        toast.error('Leveranciers laden mislukt');
      } finally {
        setLoading(false);
      }
    };

    fetchLeveranciers();
  }, [api]);

  const handleVerify = async (leverancierId, isVerified) => {
    setProcessing(true);
    try {
      await api.patch(`/admin/partners/${leverancierId}/verify`, { is_verified: isVerified });
      setLeveranciers(leveranciers.map(l => 
        l.id === leverancierId ? { ...l, is_verified: isVerified } : l
      ));
      toast.success(isVerified ? 'Leverancier geverifieerd' : 'Verificatie ingetrokken');
      setConfirmDialog({ open: false, leverancier: null });
    } catch (error) {
      toast.error('Actie mislukt');
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeClick = (leverancier) => {
    setConfirmDialog({ open: true, leverancier });
  };

  const filteredLeveranciers = leveranciers.filter(leverancier => {
    const matchesSearch = leverancier.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      leverancier.contact_email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'verified' && leverancier.is_verified) ||
      (filterStatus === 'pending' && !leverancier.is_verified);
    return matchesSearch && matchesStatus;
  });

  const getTierBadge = (tier) => {
    const variants = {
      free: 'border-neutral-200 bg-white text-neutral-600',
      premium: 'border-transparent bg-yellow-50 text-yellow-700',
      elite: 'border-transparent bg-purple-50 text-purple-700',
    };
    const labels = {
      free: 'Free',
      premium: 'Premium',
      elite: 'Elite',
    };
    return (
      <Badge className={variants[tier] || variants.free}>
        {labels[tier] || 'Free'}
      </Badge>
    );
  };

  // Stats
  const totalLeveranciers = leveranciers.length;
  const verifiedCount = leveranciers.filter(l => l.is_verified).length;
  const pendingCount = leveranciers.filter(l => !l.is_verified).length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-black" data-testid="admin-leveranciers-title">
              Leveranciers
            </h1>
          </div>
          <Button 
            className="bg-black text-white hover:bg-neutral-800"
            onClick={() => toast.info('Leverancier toevoegen - Binnenkort beschikbaar')}
            data-testid="add-leverancier-btn"
          >
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Toevoegen
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-black">{totalLeveranciers}</p>
              <p className="text-xs text-neutral-500">Totaal</p>
            </CardContent>
          </Card>
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{verifiedCount}</p>
              <p className="text-xs text-neutral-500">Geverifieerd</p>
            </CardContent>
          </Card>
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-orange-600">{pendingCount}</p>
              <p className="text-xs text-neutral-500">In afwachting</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
            <Input
              type="text"
              placeholder="Zoek leverancier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-neutral-200 focus:border-black focus:ring-black"
              data-testid="leverancier-search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[180px] border-neutral-200">
              <Filter className="h-4 w-4 mr-2 text-neutral-400" strokeWidth={1.5} />
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle statussen</SelectItem>
              <SelectItem value="verified">Geverifieerd</SelectItem>
              <SelectItem value="pending">In afwachting</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Leveranciers Table */}
        <Card className="border-neutral-200 shadow-sm">
          <CardContent className="p-0">
            {filteredLeveranciers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="leveranciers-table">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="text-left text-xs font-medium text-neutral-500 p-4">Leverancier</th>
                      <th className="text-left text-xs font-medium text-neutral-500 p-4">Contact</th>
                      <th className="text-center text-xs font-medium text-neutral-500 p-4">Rating</th>
                      <th className="text-center text-xs font-medium text-neutral-500 p-4">Status</th>
                      <th className="text-center text-xs font-medium text-neutral-500 p-4">Tier</th>
                      <th className="text-right text-xs font-medium text-neutral-500 p-4">Acties</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {filteredLeveranciers.map((leverancier) => (
                      <tr 
                        key={leverancier.id} 
                        className="hover:bg-neutral-50 cursor-pointer"
                        onClick={() => setDetailDialog({ open: true, leverancier })}
                        data-testid={`leverancier-row-${leverancier.id}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                              <Truck className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-black">{leverancier.company_name}</p>
                              <p className="text-xs text-neutral-500">{leverancier.expertise?.slice(0, 2).join(', ')}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="text-sm text-neutral-600">{leverancier.contact_email}</p>
                        </td>
                        <td className="p-4 text-center">
                          <span className="flex items-center justify-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" strokeWidth={1.5} />
                            <span className="text-sm font-medium">{leverancier.rating?.toFixed(1) || '0.0'}</span>
                            <span className="text-xs text-neutral-400">({leverancier.review_count || 0})</span>
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          {leverancier.is_verified ? (
                            <Badge className="border-transparent bg-green-50 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" strokeWidth={1.5} />
                              Geverifieerd
                            </Badge>
                          ) : (
                            <Badge className="border-transparent bg-orange-50 text-orange-700">
                              In afwachting
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {getTierBadge(leverancier.tier)}
                        </td>
                        <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setDetailDialog({ open: true, leverancier })}
                              data-testid={`view-button-${leverancier.id}`}
                            >
                              <Eye className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                            {leverancier.is_verified ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handleRevokeClick(leverancier)}
                                data-testid={`revoke-button-${leverancier.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />
                                Intrekken
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleVerify(leverancier.id, true)}
                                data-testid={`verify-button-${leverancier.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />
                                Verifiëren
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon={Truck}
                title={searchQuery || filterStatus !== 'all' ? 'Geen resultaten' : 'Nog geen leveranciers'}
                description={
                  searchQuery || filterStatus !== 'all'
                    ? 'Probeer een andere zoekopdracht of filter'
                    : 'Leveranciers die zich registreren verschijnen hier. U kunt ze vervolgens verifiëren.'
                }
                actionLabel={searchQuery || filterStatus !== 'all' ? 'Filters wissen' : undefined}
                onAction={searchQuery || filterStatus !== 'all' ? () => { setSearchQuery(''); setFilterStatus('all'); } : undefined}
              />
            )}
          </CardContent>
        </Card>

        {/* Confirm Revoke Dialog */}
        <ConfirmDialog
          open={confirmDialog.open}
          onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
          title="Verificatie intrekken"
          description={`Weet u zeker dat u de verificatie van "${confirmDialog.leverancier?.company_name}" wilt intrekken? De leverancier kan dan geen nieuwe offertes meer maken totdat u ze opnieuw verifieert.`}
          confirmLabel="Ja, intrekken"
          cancelLabel="Annuleren"
          variant="warning"
          loading={processing}
          onConfirm={() => handleVerify(confirmDialog.leverancier?.id, false)}
        />

        {/* Detail Dialog */}
        <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ ...detailDialog, open })}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                  <Truck className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                </div>
                {detailDialog.leverancier?.company_name}
              </DialogTitle>
              <DialogDescription>
                Leverancier details
              </DialogDescription>
            </DialogHeader>
            
            {detailDialog.leverancier && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-2">
                  {detailDialog.leverancier.is_verified ? (
                    <Badge className="bg-green-50 text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" strokeWidth={1.5} />
                      Geverifieerd
                    </Badge>
                  ) : (
                    <Badge className="bg-orange-50 text-orange-700">In afwachting</Badge>
                  )}
                  {getTierBadge(detailDialog.leverancier.tier)}
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-neutral-600">{detailDialog.leverancier.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 p-4 bg-neutral-50 rounded-lg">
                    <div>
                      <p className="text-xs text-neutral-500">Rating</p>
                      <p className="text-sm font-medium flex items-center gap-1">
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        {detailDialog.leverancier.rating?.toFixed(1) || '0.0'} ({detailDialog.leverancier.review_count || 0})
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Offertes</p>
                      <p className="text-sm font-medium">{detailDialog.leverancier.quote_count || 0}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-neutral-600">
                      <Mail className="h-4 w-4" strokeWidth={1.5} />
                      {detailDialog.leverancier.contact_email}
                    </div>
                    {detailDialog.leverancier.phone && (
                      <div className="flex items-center gap-2 text-sm text-neutral-600">
                        <Phone className="h-4 w-4" strokeWidth={1.5} />
                        {detailDialog.leverancier.phone}
                      </div>
                    )}
                    {detailDialog.leverancier.website && (
                      <div className="flex items-center gap-2 text-sm text-neutral-600">
                        <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                        <a href={detailDialog.leverancier.website} target="_blank" rel="noopener noreferrer" className="underline">
                          {detailDialog.leverancier.website}
                        </a>
                      </div>
                    )}
                  </div>

                  {detailDialog.leverancier.expertise?.length > 0 && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-2">Expertise</p>
                      <div className="flex flex-wrap gap-1.5">
                        {detailDialog.leverancier.expertise.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs border-neutral-200 text-neutral-600">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailDialog({ open: false, leverancier: null })}>
                Sluiten
              </Button>
              {detailDialog.leverancier && (
                detailDialog.leverancier.is_verified ? (
                  <Button
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => {
                      setDetailDialog({ open: false, leverancier: null });
                      handleRevokeClick(detailDialog.leverancier);
                    }}
                  >
                    <XCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />
                    Intrekken
                  </Button>
                ) : (
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => {
                      handleVerify(detailDialog.leverancier.id, true);
                      setDetailDialog({ open: false, leverancier: null });
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />
                    Verifiëren
                  </Button>
                )
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminLeveranciers;
